import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import { getOrganizationOpeningStatus } from 'src/utils/getOrganizationOpeningStatus';
import { GetOrganizationSearchDto as OrganizationSearchDto } from 'woofwoof-api';

interface SearchResultsListCardOpeningHoursModalProps {
  organization: Pick<OrganizationSearchDto, 'workingHours' | 'timeOff'> &
    Partial<OrganizationSearchDto>;
  isMobile?: boolean;
}

export default function SearchResultsListCardOpeningHoursModal({
  organization,
}: SearchResultsListCardOpeningHoursModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const longWeekdayKeys = useMemo(
    () =>
      [
        'calendar.weekdayLongMon',
        'calendar.weekdayLongTue',
        'calendar.weekdayLongWed',
        'calendar.weekdayLongThu',
        'calendar.weekdayLongFri',
        'calendar.weekdayLongSat',
        'calendar.weekdayLongSun',
      ] as const,
    [],
  );
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));

  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);

  // Check if there's a time off for today
  const getTodayTimeOff = () => {
    const timeOff = (organization.timeOff || []) as Array<{
      startDate: string;
      endDate: string;
      startTime?: string;
      endTime?: string;
    }>;
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);

    return timeOff.filter((off) => {
      const offStart = new Date(off.startDate);
      const offEnd = new Date(off.endDate);
      return offStart <= todayEnd && offEnd >= todayStart;
    });
  };

  const todayTimeOffs = getTodayTimeOff();

  const openingStatus = getOrganizationOpeningStatus(
    organization.workingHours,
    todayTimeOffs,
  );

  // Helper function to check if a specific date has time off
  const getTimeOffForDate = (date: Date) => {
    const timeOff = (organization.timeOff || []) as Array<{
      startDate: string;
      endDate: string;
      startTime?: string;
      endTime?: string;
    }>;
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    return timeOff.filter((off) => {
      const offStart = new Date(off.startDate);
      const offEnd = new Date(off.endDate);
      return offStart <= dayEnd && offEnd >= dayStart;
    });
  };

  // Helper function to format working hours for display
  const formatWorkingHours = () => {
    const weekdayNames = ['', ...longWeekdayKeys.map((key) => t(key))];

    const now = new Date();
    // Use local timezone instead of hardcoded offset
    const currentWeekday = now.getDay() === 0 ? 7 : now.getDay(); // Convert Sunday (0) to 7, keep Monday (1) as 1

    return weekdayNames.slice(1).map((dayName, index) => {
      const weekday = index + 1; // display order: 1=Monday … 7=Sunday
      const storedWeekday = weekday % 7; // stored: 0=Sunday … 6=Saturday
      const dayHours =
        organization.workingHours?.filter(
          (slot) => slot.weekday === storedWeekday,
        ) || [];
      const isToday = weekday === currentWeekday;

      // Calculate the date for this weekday (relative to current week)
      const dayDate = new Date(now);
      const daysUntilWeekday = weekday - currentWeekday;
      dayDate.setDate(dayDate.getDate() + daysUntilWeekday);

      // Check for time off on this day
      const dayTimeOffs = getTimeOffForDate(dayDate);
      const hasFullDayOff = dayTimeOffs.some(
        (off) => !off.startTime || !off.endTime,
      );
      const partialTimeOffs = dayTimeOffs.filter(
        (off) => off.startTime && off.endTime,
      );

      let hoursText = t('common.closed');
      let hasTimeOff = false;

      // Helper to convert time string to minutes for comparison
      const timeToMinutes = (time: string) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
      };

      // Helper to convert minutes back to time string
      const minutesToTime = (mins: number) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
      };

      if (hasFullDayOff) {
        hoursText = t('common.dayOff');
        hasTimeOff = true;
      } else if (partialTimeOffs.length > 0 && dayHours.length > 0) {
        // Multiple partial time offs - merge them and calculate available periods
        // Sort time offs by start time
        const sortedTimeOffs = partialTimeOffs
          .map((off) => ({
            start: timeToMinutes(off.startTime!),
            end: timeToMinutes(off.endTime!),
          }))
          .sort((a, b) => a.start - b.start);

        // Build available periods by subtracting all time offs from working hours
        const adjustedPeriods: string[] = [];

        dayHours.forEach((slot) => {
          const slotStart = slot.start;
          const slotEnd = slot.end;
          let currentStart = timeToMinutes(slotStart);
          const slotEndMins = timeToMinutes(slotEnd);

          // Process each time off period
          for (const timeOff of sortedTimeOffs) {
            // If current position is past slot end, stop
            if (currentStart >= slotEndMins) break;

            // If time off starts after current position, add available period
            if (timeOff.start > currentStart) {
              const periodEnd = Math.min(timeOff.start, slotEndMins);
              adjustedPeriods.push(
                `${minutesToTime(currentStart)}-${minutesToTime(periodEnd)}`,
              );
            }

            // Move current position past this time off
            if (timeOff.end > currentStart) {
              currentStart = timeOff.end;
            }
          }

          // Add remaining period after all time offs
          if (currentStart < slotEndMins) {
            adjustedPeriods.push(
              `${minutesToTime(currentStart)}-${minutesToTime(slotEndMins)}`,
            );
          }
        });

        hoursText =
          adjustedPeriods.length > 0
            ? adjustedPeriods.join(', ')
            : t('common.dayOff');
        hasTimeOff = true;
      } else if (dayHours.length > 0) {
        // Convert UTC times to local time and group consecutive hours or show multiple periods
        hoursText = dayHours
          .map((slot) => `${slot.start}-${slot.end}`)
          .join(', ');
      }

      return {
        day: dayName,
        hours: hoursText,
        isToday,
        isClosed: dayHours.length === 0 || hasFullDayOff,
        hasTimeOff,
      };
    });
  };

  const weeklyHours = formatWorkingHours();

  return (
    <>
      <Typography
        variant="body2"
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={openModal}
      >
        {isDownLg ? openingStatus.shortTextHours : openingStatus.text}
      </Typography>
      <Dialog open={isOpen} onClose={closeModal}>
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pr={1.5}
        >
          <DialogTitle>{t('search.openingHoursTitle')}</DialogTitle>
          <IconButton onClick={closeModal}>
            <Iconify icon="ic:close" />
          </IconButton>
        </Stack>

        <DialogContent
          sx={{ maxWidth: '400px', width: '100%', pb: 3, minWidth: '300px' }}
        >
          <Stack gap={2}>
            {/* Current status */}
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={1}
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: openingStatus.isOpen
                  ? theme.palette.success.lighter
                  : theme.palette.error.lighter,
              }}
            >
              <Iconify
                icon={
                  openingStatus.isOpen
                    ? 'solar:check-circle-bold-duotone'
                    : 'solar:danger-circle-bold-duotone'
                }
                color={
                  openingStatus.isOpen
                    ? theme.palette.success.main
                    : theme.palette.error.main
                }
              />
              <Typography
                variant="body2"
                sx={{
                  color: openingStatus.isOpen
                    ? theme.palette.success.dark
                    : theme.palette.error.dark,
                  fontWeight: 500,
                }}
              >
                {openingStatus.text}
              </Typography>
            </Stack>

            {/* Weekly hours */}
            <Stack gap={1}>
              {weeklyHours.map((day) => (
                <Stack
                  key={day.day}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: 0.5,
                    color: day.isToday ? theme.palette.primary.main : 'inherit',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: day.isToday ? 600 : 400,
                      color: day.isToday
                        ? theme.palette.primary.main
                        : 'inherit',
                    }}
                  >
                    {day.day}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: day.isToday ? 600 : 400,
                      color: day.isToday
                        ? theme.palette.primary.main
                        : day.isClosed
                        ? theme.palette.text.secondary
                        : 'inherit',
                    }}
                  >
                    {day.hours}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
